import { Form, Formik } from 'formik';
import useTranslation from 'next-translate/useTranslation';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Box, Flex } from 'rebass/styled-components';
import styled from 'styled-components';
import { object, string } from 'yup';

import { CheckMarkIcon } from '@/components/Icon';
import InfoIcon from '@/components/Icon/InfoIcon';
import { Input } from '@/components/Inputs';
import FormButton from '@/components/Inputs/FormButton';
import LoadingOverlay from '@/components/LoadingOverlay';
import Modal from '@/components/Modal';
import Text from '@/components/Text';
import { toast } from '@/components/Toaster/Toaster';
import { useDocumentsList } from '@/hooks';
import { userApi } from '@/services';

const TextBox = styled(Box)``;

TextBox.defaultProps = {
  px: ['10px', '30px', '40px'],
};

const ChecklistItem = ({ children, ...props }) => (
  <Flex {...props} mb="0">
    <Box mt="5" mr="20px">
      <CheckMarkIcon />
    </Box>
    <Text fontSize={['12px', '12px', '12px']} lineHeight={['24px', '24px', '26px']} textAlign="left">
      {children}
    </Text>
  </Flex>
);
ChecklistItem.propTypes = {
  children: PropTypes.node.isRequired,
};
ChecklistItem.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  mb: '6',
};

const dialogStyle = {
  'min-width': '320px',
  'max-width': '700px',
};

const DialogButton = styled(Flex)``;
DialogButton.defaultProps = {
  px: '10px',
  width: ['100%', '100%', '50%'],
  my: ['16px', '16px', '0'],
};

const validation = object().shape({
  id: string().trim().default('').required('Bitte Dokument auswählen.'),
});

const DocumentAssignDialog = ({ requestId, onCloseClick, onSelected, projectId, taskId, sid }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common-dropzone');
  const content = t('dropzone.dropzone.dialogs.assign', {}, { returnObjects: true });
  const { t: h } = useTranslation('common-toast');
  const toaster = h('toast', {}, { returnObjects: true });

  const { data, isFetching } = useDocumentsList();

  const options = useMemo(
    () =>
      data?.map((item) => ({
        label: `${item.name}`,
        value: item.documentId,
        isDisabled: false,
      })),
    [data]
  );

  const { mutateAsync } = useMutation(
    async (docId) => {
      // --AIO-1178
      if (docId === '') {
        throw new Error('Bitte wählen Sie ein Dokument aus.');
      }

      if (requestId) {
        await userApi.post(
          '/documents/actions/assigndocument',
          {},
          {
            params: { proceedingId: requestId, documentId: docId },
          }
        );
      }

      if (projectId) {
        await userApi.post(
          `/documents/projectrelation/${projectId}/task/${taskId}/subtask/${sid}`,
          {},
          {
            params: { id_document: docId },
          }
        );
      }
    },
    {
      onSuccess: () => {
        // PROJEKTE if (requestId) {}
        queryClient.invalidateQueries('documents');
        queryClient.invalidateQueries(['request', requestId]);
        queryClient.invalidateQueries(['project']);
        onCloseClick();
        toast({
          type: 'success',
          message: toaster.requests.attach,
          dismissable: true,
          withIcon: true,
        });
      },
      onError: (err) => {
        toast({
          type: 'error',
          message: `${toaster.error} - ${err.message}`,
          dismissable: true,
          withIcon: true,
        });
      },
    }
  );

  const formikConfig = {
    initialValues: validation.cast(),
    validation,
    onSubmit: async (values) => {
      if (onSelected && values.id !== '') {
        onSelected?.(values.id);
        onCloseClick();
      }

      if (requestId) {
        await mutateAsync(values.id);
      }

      if (projectId) {
        await mutateAsync(values.id);
      }
    },
  };

  const showDialog = useMemo(() => {
    if (requestId) return !!requestId;
    if (projectId) return !!projectId;

    return false;
  }, [projectId, requestId]);

  return (
    <Modal style={dialogStyle} showDialog={showDialog} onCloseClick={onCloseClick} ariaLabel="content.title">
      <Box>
        <Text as="h2" textAlign="center" mt="0" mb={['20px', '20px', '20px']} variant="headline" color="primary">
          {content?.title}
        </Text>
        <Text variant="body" textAlign="center" my="32px">
          {content?.subtitle}
        </Text>
        <Flex
          sx={{
            bg: `#f8f3d6`,
            borderRadius: '4px',
            border: `solid 1px`,
            borderColor: `#ede6ca`,
          }}
          p={['6', '6', '6']}
          mt={['7', '7', '7']}
        >
          <Box pr="25px" pt="3px">
            <InfoIcon color="#856404" />
          </Box>
          <Text variant="subheadCard" mb={['0', '0', '0']} color="#856404">
            {content?.infoText}
          </Text>
        </Flex>

        <LoadingOverlay status={!isFetching} loadOnMount>
          <Formik {...formikConfig}>
            <Form>
              <Input
                type="select"
                name="id"
                title={content?.selectLabel}
                label={content?.selectLabel}
                options={options}
                placeholder="Dokument auswählen..."
                width={['100%']}
              />
              <Box mt="210px" textAlign={['center', 'right', 'right']}>
                <FormButton Icon="" text={content?.action} variant="primary" />
              </Box>
            </Form>
          </Formik>
        </LoadingOverlay>
      </Box>
    </Modal>
  );
};

DocumentAssignDialog.propTypes = {
  dataAssign: PropTypes.shape({}),
  requestId: PropTypes.string,
  onCloseClick: PropTypes.func,
  onSelected: PropTypes.func,
  projectId: PropTypes.string,
  taskId: PropTypes.string,
  sid: PropTypes.string,
};

DocumentAssignDialog.defaultProps = {
  dataAssign: undefined,
  requestId: '',
  onCloseClick: undefined,
  onSelected: undefined,
  projectId: undefined,
  taskId: undefined,
  sid: undefined,
};

export default DocumentAssignDialog;
